import React from 'react'

import get from 'lodash/get'

import type { TypographyOwnProps } from '@mui/material/Typography'
import { deepOrange, green, orange, teal } from '@mui/material/colors'
import { createTheme } from '@mui/material/styles'
import type { CssVarsTheme } from '@mui/material/styles'
import type { PaletteColorOptions } from '@mui/material/styles/createPalette'
import type { Theme } from '@mui/material/styles/createTheme'

export const customPalette = {
  paragraphGrey: '#7E7E7E',
  red80: '#9F0F11',
  red70: '#C62E31',
  red20: '#FFE3E4',
  red30: '#FFCDCF',
  red10: '#FFF2F2',
  orange20: '#FFF0DB',
  orange40: '#FEDBAD',
  orange50: '#F6BF83',
  orange100: '#963F0F',
  brown40: '#D5B6AF',
  green10: '#E8F8EA',
  green30: '#BFEAC5',
  green80: '#14751F',

  foreground: {
    contrast: '#FFFFFF',
    default: '#1A1A1A',
    secondary: '#3D3D3D',
    tertiary: '#7E7E7E',
    disabled: '#E0E0E0',
    grey1: '#959595',
  },
  border: {
    default: '#EBEBEB',
    secondary: '#E0E0E0',
    tertiary: '#BEBEBE',
    contrast: '#7E7E7E',
    contrastHigh: '#252525',
  },
  background: {
    default: '#FFFFFF',
    contrast: '#E0E0E0',
    secondary: '#F8F8F8',
    tertiary: '#F1F1F1',
    container: '#252525',
  },
  signal: {
    error: '#C62E31',
    errorBg: '#FFF2F2',
    success: '#189D28',
    warning: '#CD6717',
    warningBg: '#FFF8E9',
  },
  // Inline Banner, UI Component Library node 6958:4457. Grouped per variant because the component
  // pairs each background with its own border and icon, and nothing else uses those pairings.
  banner: {
    informationBg: '#EFF4FF',
    informationBorder: '#DFE6FF',
    informationIcon: '#4359C9',
    attentionBg: '#FFF8E9',
    attentionBorder: '#FFF0DB',
    attentionIcon: '#DE8538',
  },
  icons: {
    default: '#1A1A1A',
    secondary: '#7E7E7E',
    brand: '#188E9D',
  },
  secondary: {
    accent: {
      lightTeal: '#E8F4F5',
      teal: '#F3F9FA',
      lightGraceOrange: '#FFF8E9',
      mediumGraceOrange: '#FFF0DB',
      lightGraceGreen: '#E8F8F8',
      tealLow: '#E8F8F8',
      mediumGraceGreen: '#D4EFF0',
      lightRust: '#FBE8D5',
      lightPetrol: '#D1E8EB',
      brown: '#E8D6D2',
      brownHigh: '#E8D6D2',
      other: '#E8AB03',
      orangeMedium: '#FFE9CB',
    },
  },
  primary: {
    accent: {
      teal: '#188E9D',
      teal5p: '#188E9D0D',
      teal10p: '#188E9D1A',
      tealGradient: 'linear-gradient(90deg, #33AECA 0%, #195664 100%)',
      tealHigh: '#195664',
      graceOrange: '#EEA358',
      graceGreen: '#17BDC3',
      yellow: '#E6BF52',
      rust: '#B34B12',
      maroon: '#5F1707',
      petrol: '#195664',
      other: '#FFFAEE',
      orangeLow: '#DE8538',
      brownHigh: '#5F1707',
      success: '#189D28',
      inactive: '#BEBEBE',
    },
  },
  gridItem: {
    background: {
      contrast: {
        start: '#F2F2F2',
        end: '#E1E1E1',
      },
      default: {
        start: '#FBFBFB',
        end: '#F5F5F5',
      },
    },
  },
  linearProgress: {
    background: '#b2dfdb',
    barBackgroundColor: '#3297A5',
  },
  stepper: {
    background: '#3297a514',
  },
  accent: {
    light: teal[200],
    main: teal[300],
    dark: teal[400],
  },
  panel: {
    selected: {
      row: deepOrange[50],
    },
  },
  success: {
    main: green[500],
  },
  warning: {
    main: orange[500],
  },
  danger: {
    main: '#D00026',
  },
  black: '#000000',
  white: '#FFFFFF',
  lightGrey: '#1a1a1a',
  lightGrey2: '#e7e7e7',
  lightGrey3: '#ebebeb',
  lightGrey4: '#E0E0E0',
  lightGrey5: '#ebebeb',
  lightGrey6: '#f8f8f8',
  lightGrey7: '#7e7e7e',
  lightGrey8: '#F8F8F880',
  // todo remove bottom greys kas
  lightGrey9: '#F3F3F3',
  lightGrey10: '#F7F7F7',
  lightGrey11: '#EAEAEA',
  lightGrey12: 'rgba(0, 0, 0, 0.08)',
  lightGrey13: 'rgba(0, 0, 0, 0.32)',
  lightGrey14: 'rgba(0, 0, 0, 0.06)',
  lightGrey15: 'rgba(0, 0, 0, 0.65)',
  lightGrey16: 'rgba(0, 0, 0, 0.87)',
  lightGrey17: '#555555',
  lightGrey18: '#fbfbfb',
  purple: {
    p10: '#F9EFFF',
    p20: '#F1DFFF',
    p30: '#E1C1F5',
    p60: '#8A43C9',
    p80: '#55048B',
  },
  teal: {
    t10: '#E8F8F8',
    t30: '#BFE9EA',
    t40: '#9BDCDE',
    t90: '#195664',
    t100: '#09404D',
  },
  green: {
    g10: '#E8F8EA',
    g30: '#BFEAC5',
    g40: '#9BDE9E',
    g80: '#14751F',
  },
  blue: {
    b10: '#EFF4FF',
    b80: '#16048B',
  },
  grey: {
    g20: '#F1F1F1',
    g40: '#E0E0E0',
    g50: '#D3D3D3',
    g70: '#9E9E9E',
    g90: '#696969',
  },
  transparentWhite: 'rgba(255, 255, 255, .6)',
  transparent: 'transparent',
  linkBlue: '#3297A5',
  actionLinkBlue: '#0067A8',
  lightBlue: '#B9DEF5',
  lightBlue2: '#D5F2FF',
  bgConicGradient: 'conic-gradient(from 124deg at 50% 38.39%, #fbfbfb 0deg, #f5f5f5 360deg)',
  status: {
    unknown: {
      background: 'grey',
    },
    stopped: {
      background: '#b00020',
    },
    pending: {
      background: '#e36f32',
    },
    running: {
      background: '#f1f1f1',
    },
    failed: {
      background: '#B00020',
    },
    success: {
      background: '#07bc0c',
    },
  },
  notificationMessage: {
    warning: 'rgba(227, 111, 50, 0.12)',
    error: 'rgba(176, 0, 32, 0.06)',
    errorDarker: 'rgba(176, 0, 32, 0.24)',
    success: green[500],
    progressBg: '#d4eff0',
  },
  auditorStatsColors: {
    danger: '#FF5252',
    warning: '#FFE082',
    optimal: '#ACE9CF',
  },
  menu: {
    background: '#FFFFFF',
    topBar: '#2B4151',
    leftIcons: '#7E7E7E',
    topIcons: '#ffffff',
    text: '#ffffff',
    selected: 'rgba(227, 111, 50, 0.2)',
  },
  levels: {
    danger: '#FF0000',
    warning: '#FFA800',
    optimal: '#00BC58',
  },
  importance: {
    all: '#E0E0E0',
    low: '#EEA358',
    medium: '#B34B12',
    high: '#5F1707',
  },
  riskLevel: {
    low: '#4CAF50',
    medium: '#FF8F00',
    high: '#E91E63',
  },
  phases: {
    nonStarted: '#e36f32',
    draft: '#2b4151',
    completed: '#5bacb7',
  },
  achievements: {
    obtained: '#D5F2FF',
    retry: '#FFE9EE',
  },
}

const chipsPalette = {
  green: '#3297A5',
  red: '#D00026',
  lightGreen: 'rgba(50, 151, 165, 0.12)',
  lightRed: 'rgba(208, 0, 38, 0.16)',
  orange: '#E36F32',
  black: '#494949',
  default: 'rgba(0, 0, 0, 0.06)',
  hoverColors: {
    green: 'rgba(50, 151, 165, 0.8)',
    red: 'rgba(208, 0, 38, 0.8)',
    lightGreen: 'rgba(50, 151, 165, 0.24)',
    lightRed: 'rgba(208, 0, 38, 0.24)',
    orange: 'rgba(227, 111, 50, 0.8)',
    black: 'rgba(0, 0, 0, 0.8)',
    default: 'rgba(0, 0, 0, 0.12)',
  },
}

const chartsPalette = {
  colors: {
    default: '#5E77FF',
  },
  pie: {
    colors: ['#5072F8', '#6583FA', '#6E8AFB', '#7F97FB', '#94A8FB', '#9EB0FC', '#AFBEFC', '#C1CCFD', '#CAD4FD'],
    pie2Color: '#455A64',
    pie2BgColor: '#E9E9E9',
  },
  bar: {
    color: '#1F78EC',
  },
  referenceLine: '#7E7E7E',
}

const sizes = {
  sidebar: {
    miniMode: '72px',
    fullWidth: 'auto',
  },
  scrollbar: {
    size: '6px',
    borderRadius: '3px',
  },
  header: {
    size: '64px',
  },
  dataTable: {
    header: '48px',
    actionBar: '72px',
  },
  formLayout: {
    leftColumnWidth: 212,
    narrowFormBoxWidth: 448,
    formBoxWidth: 684,
  },
}

declare module '@mui/material/styles' {
  interface Theme {
    sizes: typeof sizes
  }

  interface ThemeOptions {
    sizes?: typeof sizes
  }

  interface Palette {
    custom: typeof customPalette
    charts: typeof chartsPalette
    chips: typeof chipsPalette
  }

  interface PaletteOptions {
    custom?: typeof customPalette
    charts?: typeof chartsPalette
    chips?: typeof chipsPalette
    blueGrey: PaletteColorOptions
  }

  interface TypographyVariants {
    body3: React.CSSProperties
    body4: React.CSSProperties
    body5: React.CSSProperties
    paragraph: React.CSSProperties
    subtitle3: React.CSSProperties
  }

  interface TypographyVariantsOptions {
    body3: React.CSSProperties
    body4: React.CSSProperties
    body5: React.CSSProperties
    paragraph?: React.CSSProperties
    subtitle3: React.CSSProperties
  }
}

declare module '@mui/material/Typography' {
  interface TypographyOwnProps {
    lineClamp?: number
  }
  interface TypographyPropsVariantOverrides {
    paragraph: true
    body3: true
    body4: true
    body5: true
    subtitle3: true
  }
}

declare module '@mui/material/Badge' {
  interface BadgePropsColorOverrides {
    [key: string]: true
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    tertriary: true
    blueGrey: true
  }
  interface ButtonPropsVariantOverrides {
    pureText: true
    circle: true
    tertriary: true
    blueGrey: true
  }
  interface ButtonPropsSizeOverrides {
    smallMedium: true
  }
}

declare module '@mui/material/Button/buttonClasses' {
  interface ButtonClasses {
    sizeSmallMedium: string
  }
}

declare module '@mui/material/IconButton' {
  interface IconButtonOwnProps {
    variant?: 'standard' | 'contained' | 'outlined' | 'outlined-square' | 'contained-mid'
  }
}
declare module '@mui/material/SvgIcon' {
  interface SvgIconPropsSizeOverrides {
    xsmall: true
  }
}

declare module '@mui/material/InputBase' {
  interface InputBasePropsColorOverrides {
    searchBar: true
  }
}
declare module '@mui/material/TextField' {
  interface TextFieldPropsColorOverrides {
    searchBar: true
  }
}

// follow this structure https://material-ui.com/customization/default-theme/
// put custom variables in `custom` property of the corresponding piece of the theme
export const getTheme = (colors?: Record<string, Record<string, string>>) => {
  return createTheme({
    components: {
      MuiAppBar: {
        styleOverrides: {
          root: {
            background: colors?.menu?.background,
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            backgroundColor: customPalette.linearProgress.background,
          },
          barColorPrimary: {
            backgroundColor: customPalette.linearProgress.barBackgroundColor,
          },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
            '&$expanded': {
              margin: 0,
              boxShadow: '0 4px 5px 0 rgba(0,0,0,0.14), 0 1px 10px 0 rgba(0,0,0,0.12), 0 2px 4px -1px rgba(0,0,0,0.2)',
            },
            '&.Mui-expanded': {
              margin: 0,
            },
          },
          expanded: {},
        },
      },
      MuiAccordionSummary: {
        styleOverrides: {
          content: {
            margin: '4px 0',
            alignItems: 'center',
            '&.Mui-expanded': {
              margin: '4px 0',
            },
          },
          root: {
            padding: '24px',
            minHeight: '56px',
            '&$expanded': {
              borderBottom: '1px solid rgba(0, 0, 0, 0.16)',
            },
          },
          expanded: {},
        },
      },
      MuiAccordionDetails: {
        styleOverrides: {
          root: {
            padding: '16px 24px 24px',
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            padding: 6,
            color: customPalette.border.tertiary,
            '&:hover': {
              color: customPalette.border.contrast,
            },
            '&.Mui-checked': {
              color: customPalette.primary.accent.teal,
            },
          },
        },
      },
      MuiSvgIcon: {
        variants: [
          {
            props: {
              fontSize: 'large',
            },
            style: {
              fontSize: '40px',
            },
          },
          {
            props: {
              fontSize: 'small',
            },
            style: {
              fontSize: '18px',
            },
          },
          {
            props: {
              fontSize: 'xsmall',
            },
            style: {
              fontSize: '16px',
            },
          },
        ],
        styleOverrides: {
          root: {
            fontSize: 24,
          },
        },
      },
      MuiListItem: {
        styleOverrides: {
          root: {
            paddingTop: 9,
            paddingBottom: 9,
          },
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            minWidth: 'auto',
          },
        },
      },
      MuiListItemText: {
        styleOverrides: {
          root: {
            padding: '0 16px',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            height: '28px',
            backgroundColor: 'rgba(0, 0, 0, 0.06)',
            color: 'rgba(0, 0, 0, 0.87)',
            '&.MuiChip-filledError': {
              color: '#D00026',
            },
            fontSize: '12.5px',
            letterSpacing: '0.23px',
            lineHeight: '17px',
            borderRadius: '2px',
            border: '0.5px solid',
            borderColor: customPalette.border.tertiary,
          },
          outlined: {
            borderColor: '#3297A5',
          },
          sizeSmall: {
            fontSize: '10px',
            lineHeight: '16px',
            letterSpacing: '-0.1px',
          },
          deleteIcon: {
            color: customPalette.icons.default,
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            backgroundColor: customPalette.background.default,
            '& > fieldset': {
              borderColor: customPalette.border.default,
            },
            height: '40px',
            fontSize: '1.1rem',
          },
          input: {
            '&::placeholder': {
              color: customPalette.foreground.tertiary,
              opacity: 1,
            },
          },
          inputMultiline: {
            '&::placeholder': {
              color: customPalette.foreground.tertiary,
              opacity: 1,
            },
          },
          multiline: {
            height: 'unset',
          },
          formControl: {
            marginBottom: '8px',
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            fontSize: '11px',
            '&.Mui-error': {
              color: '#b00020 !important',
            },
          },
          contained: {
            margin: '-6px 0 0',
            lineHeight: '10px',
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontWeight: 'normal',
            color: customPalette.foreground.default,
            fontSize: '14px',
            '&.Mui-selected': {
              backgroundColor: customPalette.background.secondary,
            },
            '&.Mui-disabled': {
              backgroundColor: customPalette.background.default,
              color: customPalette.foreground.disabled,
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            fontSize: '14px',
            fontWeight: '400',
            lineHeight: '20px',
            color: customPalette.foreground.default,
          },
          icon: {
            position: 'absolute',
            top: 'calc(50% - .7em)',
            right: '8px',
            color: customPalette.foreground.default,
          },
          root: {
            '&:hover': {
              background: customPalette.lightGrey6,
            },
            height: '40px',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: customPalette.lightGrey3,
            },
            '&:hover:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline': {
              borderColor: customPalette.lightGrey5,
            },
            '&:active': {
              color: customPalette.lightGrey,
            },
            '&:active .MuiOutlinedInput-notchedOutline': {
              borderColor: customPalette.lightGrey5,
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderColor: 'rgba(255,0,0, 0.6)',
            input: {
              padding: '12px 10px',
              letterSpacing: '-0.14px',
            },
            // TODO: remove when issue solved: a workaround for this issue: https://github.com/mui/material-ui/issues/32934
            'input[required] + fieldset > legend > span': {
              paddingRight: 0,
              margin: '-1px',
            },
            '&.Mui-error': {
              color: customPalette.foreground.default,
              '& $.MuiOutlinedInput-notchedOutline': {
                borderColor: `${customPalette.signal.error} !important`,
              },
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderWidth: '1px',
            },
            '&.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
              borderColor: `${customPalette.icons.secondary} !important`,
            },
            '&.Mui-focused': {
              backgroundColor: customPalette.secondary.accent.tealLow,
            },
            '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
              borderColor: customPalette.border.default,
            },
            '&.Mui-disabled .MuiOutlinedInput-input': {
              '-webkit-text-fill-color': customPalette.foreground.disabled,
            },
            '&:hover:not(.Mui-disabled)': {
              backgroundColor: customPalette.secondary.accent.tealLow,
            },
            '&:hover:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline': {
              borderColor: customPalette.border.contrast,
            },
          },
          input: {
            fontSize: '1rem',
            borderRadius: '4px',
            '&::placeholder': {
              color: customPalette.foreground.tertiary,
              opacity: 1,
            },
          },
        },
        variants: [
          {
            props: { color: 'searchBar' },
            style: {
              background: customPalette.background.default,
              svg: {
                color: customPalette.icons.secondary,
              },
              '&.Mui-focused': {
                backgroundColor: customPalette.background.secondary,
                svg: {
                  color: customPalette.icons.default,
                },
              },
              '&:hover:not(.Mui-disabled)': {
                backgroundColor: customPalette.background.secondary,
              },
              '&.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
                borderRadius: '4px 4px 0 0',
                border: `1px solid ${customPalette.border.default} !important`,
                borderBottom: '0px !important',
                boxShadow: '0 4px 30px 0 rgba(0, 0, 0, 0.10)',
              },
              '&:hover:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline': {
                borderColor: customPalette.border.default,
              },
              input: {
                fontSize: '12px',
                fontWeight: 500,
              },
            },
          },
        ],
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            position: 'static',
            transform: 'unset',
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: '20px',
            letterSpacing: '-0.14px',
            color: customPalette.foreground.default,
          },
          outlined: {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '100%',
            minHeight: '16px',
            '&.Mui-focused': {
              color: `${customPalette.primary.accent.teal} !important`,
            },
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            color: 'rgba(0, 0, 0, 0.54)',
            '&.Mui-focused': {
              color: customPalette.primary.accent.teal,
            },
            '&.Mui-disabled': {
              color: 'rgba(0,0,0,0.4) !important',
            },
          },
          asterisk: {
            display: 'none',
          },
          filled: {
            color: customPalette.primary.accent.teal,
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            '&.Mui-checked': {
              color: `${customPalette.primary.accent.teal} !important`,
            },
          },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          root: {
            '&$labelPlacementStart': {
              margin: 0,
            },
          },
          label: {
            color: customPalette.foreground.default,
            fontSize: 13.6,
            letterSpacing: '0.25px',
            lineHeight: '20px',
          },
        },
      },
      MuiInput: {
        styleOverrides: {
          root: {
            lineHeight: '1.3em',
          },
        },
      },
      MuiTableSortLabel: {
        styleOverrides: {
          icon: {
            width: '12px !important',
            height: '12px !important',
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: ({ theme, ownerState }: { theme: Theme; ownerState: TypographyOwnProps }) => {
            if (ownerState.lineClamp) {
              return {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: `${ownerState.lineClamp}`,
                WebkitBoxOrient: 'vertical',
                wordWrap: 'break-word',
                wordBreak: 'normal',
              }
            }
            return {}
          },
        },
        variants: [
          {
            props: {
              fontWeight: 'medium',
            },
            style: {
              fontWeight: 500,
            },
          },
          {
            props: {
              fontWeight: 'bold',
            },
            style: {
              fontWeight: 700,
            },
          },
        ],
      },
      MuiIconButton: {
        styleOverrides: {
          root: ({ theme, ownerState }: { theme: Theme; ownerState: { variant?: string } }) => {
            switch (ownerState.variant) {
              case 'outlined-square':
                return {
                  borderRadius: '4px',
                  border: `1px solid ${theme.palette.custom.lightGrey3}`,
                  color: theme.palette.custom.lightGrey7,
                  '&:hover': {
                    background: theme.palette.custom.lightGrey6,
                  },
                  '&:active': {
                    background: theme.palette.custom.lightGrey8,
                    color: theme.palette.custom.lightGrey,
                  },
                }
              case 'contained':
                return {
                  background: '#252525',
                  color: '#17bdc3',
                  '&:hover': {
                    background: '#252525',
                    color: '#17bdc3',
                    boxShadow: '0px 0px 0px 2px #1A1A1A33',
                  },
                  border: '1px solid #252525',
                  borderRadius: '50px',
                  padding: '18px',
                  boxShadow: 'none',
                  '&.Mui-disabled ': {
                    background: '#252525',
                    color: '#17bdc3',
                    opacity: '30%',
                  },
                  colors: ownerState.variant,
                }
              case 'contained-mid':
                return {
                  background: theme.palette.custom.background.contrast,
                  color: theme.palette.custom.icons.default,
                  '&:hover': {
                    background: theme.palette.custom.background.contrast,
                  },
                  border: 'none',
                  borderRadius: '50px',
                  padding: '18px',
                  boxShadow: 'none',
                  '&.Mui-disabled ': {
                    opacity: '30%',
                  },
                  colors: ownerState.variant,
                }
              default:
                return {}
            }
          },
          sizeMedium: {
            padding: '10px',
            '>*:nth-of-type(1)': {
              fontSize: '24px',
            },
          },
          sizeLarge: {
            '>*:nth-of-type(1)': {
              fontSize: '40px',
            },
          },
        },
      },
      MuiBadge: {
        variants: [
          {
            props: { variant: 'dot' },
            style: (
              {
                theme,
                color,
                ...ownerState
              }: { theme: Omit<Theme, 'components' | 'palette'> & CssVarsTheme; color?: string },
              ...rest
            ) => {
              if (color) {
                return {
                  '& .MuiBadge-dot': {
                    backgroundColor: get(theme.palette, color),
                  },
                }
              }
            },
          },
        ],
      },
      MuiButton: {
        variants: [
          {
            props: { variant: 'text' },
            style: {
              background: '#252525',
              color: '#fff',
              '&.Mui-disabled ': {
                color: '#fff',
                background: '#252525',
              },
            },
          },
          {
            props: { variant: 'pureText' },
            style: {
              padding: '0 !important',
              fontSize: '14px',
              background: 'transparent',
              '&:hover': {
                letterSpacing: '-0.1px',
                fontWeight: '700',
              },
              '&:active': {
                boxShadow: 'unset',
              },
            },
          },
          {
            props: { variant: 'pureText', color: 'primary' },
            style: {
              fontWeight: '500',
              color: customPalette.foreground.default,
              '&.Mui-disabled ': {
                color: customPalette.foreground.disabled,
              },
            },
          },
          {
            props: { variant: 'pureText', color: 'secondary' },
            style: {
              fontWeight: '400',
              color: customPalette.foreground.tertiary,
              '&:hover': {
                fontWeight: '500',
              },
              '&.Mui-disabled ': {
                color: customPalette.foreground.disabled,
              },
            },
          },
          {
            props: { variant: 'contained' },
            style: {
              '&.Mui-disabled ': {
                color: '#fff',
                background: '#252525',
              },
              '&:hover': {
                background: '#252525',
                color: '#fff',
              },
              background: '#252525',
              color: '#fff',
            },
          },
          {
            props: { variant: 'contained', color: 'error' },
            style: {
              background: customPalette.signal.errorBg,
              color: customPalette.signal.error,
              '&:hover': {
                color: customPalette.signal.error,
                background: customPalette.red20,
              },
              '.MuiButton-icon': {
                color: 'inherit',
              },
            },
          },
          {
            props: { variant: 'outlined' },
            style: {
              border: `1px solid ${customPalette.border.contrastHigh}`,
              color: '#252525',
              '&.Mui-disabled ': {
                '.MuiButton-icon': {
                  color: 'inherit',
                },
              },
            },
          },
          {
            props: { variant: 'outlined', color: 'secondary' },
            style: {
              border: `1px solid ${customPalette.primary.accent.teal}`,
              color: customPalette.primary.accent.teal,
              boxShadow: 'none',
              '&.Mui-disabled ': {
                color: customPalette.primary.accent.teal,
                border: `1px solid ${customPalette.primary.accent.teal}`,
              },
              '&:active': {
                boxShadow: '0px 0px 0px 2px #188E9D33',
              },
              '&:hover': {
                background: '#188E9D1A',
              },
              '.MuiButton-icon': {
                color: 'inherit',
              },
            },
          },
          {
            props: { variant: 'outlined', color: 'tertriary' },
            style: {
              border: `1px solid ${customPalette.border.default}`,
              color: customPalette.foreground.tertiary,
              boxShadow: 'none',
              '&:active': {
                color: customPalette.foreground.default,
              },
              '&:hover': {
                background: customPalette.background.secondary,
              },
              '.MuiButton-icon': {
                color: 'inherit',
              },
            },
          },
          {
            props: { variant: 'outlined', color: 'error' },
            style: {
              border: `1px solid ${customPalette.signal.error}`,
              background: customPalette.signal.errorBg,
              color: customPalette.red70,
              boxShadow: 'none',
              '&.Mui-disabled ': {
                color: customPalette.signal.errorBg,
                border: `1px solid ${customPalette.signal.errorBg}`,
              },
              '&:active': {
                boxShadow: '0px 0px 0px 2px #188E9D33',
              },
              '&:hover': {
                background: customPalette.red20,
              },
              '.MuiButton-icon': {
                color: customPalette.red70,
              },
            },
          },
          {
            props: { variant: 'text' },
            style: {
              background: '#F1F1F1',
              color: '#252525',
            },
          },
          {
            props: { variant: 'text', color: 'secondary' },
            style: {
              '.MuiButton-icon': {
                color: customPalette.icons.default,
              },
            },
          },
          {
            props: { variant: 'text', color: 'error' },
            style: {
              background: customPalette.signal.errorBg,
              color: customPalette.red70,
              '&:hover': {
                background: customPalette.red20,
              },
              '&:active': {
                border: 'solid 2px',
                borderColor: customPalette.red20,
              },
              '.MuiButton-icon': {
                color: 'inherit',
              },
            },
          },
        ],
        styleOverrides: {
          root: {
            fontSize: '12px',
            padding: '8px 18px 8px 18px',
            borderRadius: '4px',
            border: '1px solid transparent',
            boxShadow: 'none',
            minWidth: '84px',
            '&:active': {
              boxShadow: '0px 0px 0px 2px #1A1A1A33',
            },
            '&.Mui-disabled ': {
              color: customPalette.foreground.default,
              borderColor: customPalette.border.contrastHigh,
              opacity: '29.7%',
            },
          },
          sizeSmall: {
            height: '32px',
          },
          sizeSmallMedium: {
            height: '36px',
          },
          sizeMedium: {
            height: '40px',
          },
          sizeLarge: {
            height: '40px',
          },
          iconSizeMedium: {
            '>*:nth-of-type(1)': {
              fontSize: '24px',
            },
          },
          iconSizeLarge: {
            '>*:nth-of-type(1)': {
              fontSize: '16px',
            },
          },
          startIcon: {
            color: '#17bdc3',
          },
          endIcon: {
            color: '#17bdc3',
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            color: customPalette.paragraphGrey,
            borderColor: customPalette.border.default,
            padding: '5px 11px',
            '&.Mui-selected': {
              background: customPalette.background.secondary,
              color: customPalette.foreground.default,
            },
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          input: {
            '&.MuiAutocomplete-inputRoot': {
              minHeight: '30px',
            },
          },
          inputRoot: {
            height: 'unset',
            fontSize: '12px',
          },
          option: {
            display: 'flex',
            alignItems: 'center',
            border: '1px solid transparent',
            borderRadius: '4px',
            boxSizing: 'content-box',
            marginRight: '8px',
            paddingTop: '8px !important',
            paddingBottom: '8px !important',
            maxHeight: '20px',
            '&.Mui-focused': {
              fontWeight: 500,
              background: customPalette.background.tertiary,
            },
            '&[aria-selected="true"]': {
              backgroundColor: `${customPalette.background.tertiary} !important`, // without important it's overriden by primary.main color
            },
          },
          noOptions: {
            textAlign: 'center',
            color: customPalette.foreground.default,
            background: customPalette.background.secondary,
          },
        },
      },
      MuiBackdrop: {
        styleOverrides: {
          root: '#1A1A1AAD',
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            color: customPalette.foreground.tertiary,
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            padding: '32px 24px 24px',
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            padding: '32px 24px 0px',
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: '18px 24px 24px',
            borderTop: `1px solid ${customPalette.border.default}`,
          },
          spacing: {
            padding: '24px !important',
          },
        },
      },
      MuiCircularProgress: {
        styleOverrides: {
          root: {
            color: '#3297A5',
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            '&:last-child': {
              paddingBottom: '18px',
            },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            background: customPalette.background.container,
            padding: '12px',
            fontSize: '10px',
            fontWeight: 500,
          },
          arrow: {
            color: customPalette.background.container,
          },
        },
      },
      MuiSlider: {
        styleOverrides: {
          root: {
            marginBottom: '20px',
          },
        },
      },
      MuiSwitch: {
        variants: [
          {
            props: { size: 'small' },
            style: {
              width: 28,
              height: 16,
              '& .MuiSwitch-switchBase': {
                padding: 2,
                '&.Mui-checked': {
                  transform: 'translateX(12px)',
                },
              },
              '& .MuiSwitch-thumb': {
                width: 12,
                height: 12,
              },
              '& .MuiSwitch-track': {
                borderRadius: 8,
              },
            },
          },
          {
            props: { size: 'medium' },
            style: {
              width: 35,
              height: 20,
              '& .MuiSwitch-switchBase': {
                padding: 2,
                '&.Mui-checked': {
                  transform: 'translateX(15px)',
                },
              },
              '& .MuiSwitch-thumb': {
                width: 16,
                height: 16,
              },
              '& .MuiSwitch-track': {
                borderRadius: 10,
              },
            },
          },
          ...(['default', 'primary', 'secondary', 'error', 'warning', 'info', 'success'] as const).map(
            (color: 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success') => ({
              props: { color },
              style: ({ theme }: { theme: Theme }) => {
                const isTeal = color === 'default' || color === 'primary'
                const paletteColor =
                  color === 'default' ? undefined : (theme.palette[color] as { main?: string } | undefined)
                const main = isTeal ? customPalette.primary.accent.teal : paletteColor?.main
                const high = isTeal ? customPalette.primary.accent.tealHigh : paletteColor?.main

                return {
                  '& .MuiSwitch-thumb': {
                    backgroundColor: high,
                  },
                  '& .MuiSwitch-track': {
                    borderColor: high,
                  },
                  '& .MuiSwitch-switchBase': {
                    '&:hover .MuiSwitch-thumb': {
                      backgroundColor: main,
                    },
                    '&:hover + .MuiSwitch-track': {
                      borderColor: main,
                    },
                    '&.Mui-checked': {
                      '& + .MuiSwitch-track': {
                        backgroundColor: main,
                        borderColor: main,
                      },
                      '&:hover + .MuiSwitch-track': {
                        backgroundColor: main,
                        borderColor: main,
                      },
                    },
                    '&.Mui-disabled.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: main,
                      borderColor: main,
                    },
                  },
                }
              },
            }),
          ),
        ],
        styleOverrides: {
          root: {
            width: 42,
            height: 24,
            padding: 0,
            display: 'flex',
            margin: 'auto 10px',
          },
          switchBase: {
            padding: 2,
            color: customPalette.white,
            '&.Mui-checked': {
              transform: 'translateX(18px)',
              color: customPalette.white,
              '& .MuiSwitch-thumb': {
                backgroundColor: customPalette.background.default,
              },
              '& + .MuiSwitch-track': {
                opacity: 1,
              },
              '&:hover .MuiSwitch-thumb': {
                backgroundColor: customPalette.background.default,
              },
            },
            '&.Mui-disabled': {
              color: customPalette.white,
              '& + .MuiSwitch-track': {
                opacity: 1,
                backgroundColor: customPalette.foreground.disabled,
                borderColor: customPalette.foreground.disabled,
              },
              '&.Mui-checked + .MuiSwitch-track': {
                opacity: 0.5,
              },
              '& .MuiSwitch-thumb': {
                backgroundColor: customPalette.background.default,
              },
            },
          },
          thumb: {
            boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.15)',
            width: 20,
            height: 20,
            borderRadius: 10,
            transition: 'all 200ms',
          },
          track: {
            borderRadius: 12,
            opacity: 1,
            border: '1.5px solid transparent',
            backgroundColor: customPalette.background.default,
            boxSizing: 'border-box',
            transition: 'all 200ms',
          },
        },
      },
    },
    palette: {
      custom: {
        ...customPalette,
        menu: {
          background: colors?.menu?.background || customPalette.menu.background,
          topBar: colors?.menu?.topBar || customPalette.menu.topBar,
          leftIcons: colors?.menu?.leftIcons || customPalette.menu.leftIcons,
          topIcons: colors?.menu?.topIcons || customPalette.menu.topIcons,
          text: colors?.menu?.text || customPalette.menu.text,
          selected: colors?.menu?.selected || customPalette.menu.selected,
        },
      },
      divider: 'rgba(0, 0, 0, .16)',
      primary: {
        main: customPalette.primary.accent.graceGreen,
        light: customPalette.primary.accent.teal,
        dark: customPalette.primary.accent.petrol,
      },
      secondary: {
        light: customPalette.primary.accent.rust,
        main: customPalette.primary.accent.graceOrange,
        dark: customPalette.primary.accent.maroon,
      },
      blueGrey: {
        light: '#FFFFFF',
        main: '#546E7A',
      },
      background: {
        default: '#ffffff',
      },
      error: {
        main: customPalette.signal.error,
      },
      charts: chartsPalette,
      chips: chipsPalette,
    },
    typography: {
      fontFamily: '"Inter", "Helvetica Neue", sans-serif', // TODO change this font to proper when been bought.
      fontSize: 14,
      h1: {
        fontFamily: '"Helvetica Neue", "Inter", sans-serif',
        fontSize: '42px',
        fontWeight: 400,
        lineHeight: '50px',
        color: '#1a1a1a',
      },
      h2: {
        fontFamily: '"Helvetica Neue", "Inter", sans-serif',
        fontSize: '32px',
        fontWeight: 400,
        lineHeight: '40px',
        color: '#1a1a1a',
      },
      h3: {
        fontFamily: '"Helvetica Neue", "Inter", sans-serif',
        fontSize: '26px',
        fontWeight: 400,
        lineHeight: '34px',
        color: '#1a1a1a',
      },
      h4: {
        fontFamily: '"Helvetica Neue", "Inter", sans-serif',
        fontSize: '20px',
        fontWeight: 400,
        lineHeight: '34px',
        color: '#1a1a1a',
      },
      h5: {
        fontFamily: '"Inter", "Helvetica Neue", sans-serif', // TODO this we'll drop when redesign is done and font family can be changed in globalstyles
        fontSize: '16px',
        fontWeight: 400,
        lineHeight: '20px',
        color: '#1a1a1a',
      },
      // todo remove h6 @kas
      h6: {
        fontFamily: '"Inter", "Helvetica Neue", sans-serif',
        fontSize: '10px',
        fontWeight: 700,
        lineHeight: '14px',
        letterSpacing: '0px',
        color: '#1a1a1a',
      },
      body1: {
        fontFamily: '"Inter", "Helvetica Neue", sans-serif',
        fontSize: '12px',
        fontWeight: 400,
        lineHeight: '16px',
        color: '#1a1a1a',
      },
      body2: {
        fontFamily: '"Inter", "Helvetica Neue", sans-serif',
        fontSize: '10px',
        fontWeight: 400,
        lineHeight: '16px',
        color: '#1a1a1a',
      },
      body3: {
        fontSize: '8px',
        fontWeight: 400,
        lineHeight: 'auto',
        color: '#1a1a1a',
      },
      // todo remove body 4 kas
      body4: {
        fontFamily: '"Inter", "Helvetica Neue", sans-serif',
        fontSize: '14px',
        fontWeight: 400,
        lineHeight: '20px',
        letterSpacing: '.25px',
        color: '#667085',
      },
      // todo remove body 5 when oldtheme is removed
      body5: {
        fontFamily: '"Inter", "Helvetica Neue", sans-serif',
        fontSize: '14px',
        fontWeight: 400,
        lineHeight: '20px',
        letterSpacing: '.25px',
        color: 'rgba(0, 0, 0, 0.87)',
      },
      subtitle1: {
        fontFamily: '"Inter", "Helvetica Neue", sans-serif',
        fontSize: '12px',
        fontWeight: 400,
        lineHeight: '18px',
        color: '#1a1a1a',
      },
      // todo remove other sybtitles kas
      subtitle2: {
        fontFamily: '"Inter", "Helvetica Neue", sans-serif',
        fontSize: '13.5px',
        fontWeight: 600,
        lineHeight: '21px',
        letterSpacing: '.1px',
        color: 'rgba(0, 0, 0, 0.87)',
      },
      subtitle3: {
        fontFamily: '"Inter", "Helvetica Neue", sans-serif',
        fontSize: '16px',
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: '22px',
        letterSpacing: '0.2px',
      },
      caption: {
        fontFamily: '"Inter", "Helvetica Neue", sans-serif',
        fontSize: '14px',
        fontWeight: 400,
        lineHeight: '20px',
        color: '#1A1A1A',
      },
      overline: {
        fontFamily: '"Inter", "Helvetica Neue", sans-serif',
        fontSize: '11.53px',
        fontWeight: 600,
        lineHeight: '16px',
        letterSpacing: '2px',
        textTransform: 'uppercase',
        color: 'rgba(0, 0, 0, 0.87)',
      },
      button: {
        fontFamily: '"Inter", "Helvetica Neue", sans-serif',
        fontSize: '12px',
        lineHeight: '16px',
        fontWeight: 500,
        textTransform: 'none',
      },
      paragraph: {
        fontFamily: '"Inter", "Helvetica Neue", sans-serif',
        fontSize: '14px',
        fontWeight: 400,
        lineHeight: '20px',
        letterSpacing: '-0.14px',
      },
    },
    sizes,
  })
}
